import React, { FC, useState, useMemo, Suspense } from 'react';
import {
  Box,
  SegmentedControl,
  SegmentedControlOption,
  SegmentedControlSize,
  Button,
  ButtonAppearance,
  ButtonSize,
  Icon,
  IconSize,
  useSideSheetState,
  Text,
} from '@bechtel/gds';
import { flatMap } from 'lodash';
import { WorkPackage, UnitOfMeasure } from '@bechtel/types';
import { SuspenseLoader, usePlanning, PlanningActionType } from '@bechtel/client-common';
import { AssignWorkOwner } from '@bechtel/client-work-packages';
import { Trans } from '@lingui/macro';

interface PlanningActionsProps {
  workPackages: WorkPackage[];
  loading: boolean;
}

export const PlanningActions: FC<PlanningActionsProps> = ({ workPackages, loading }) => {
  const [selectedIndex, setSelectedIndex] = useState(1);
  const { visible, open, close } = useSideSheetState(false);

  const { selectedWpList, planningDispatch } = usePlanning();
  const primaryOwners = useMemo(() => {
    return flatMap(workPackages, 'primaryOwners');
  }, [workPackages]);

  const secondaryOwners = useMemo(() => {
    return flatMap(workPackages, 'secondaryOwners');
  }, [workPackages]);

  const otherOwners = useMemo(() => {
    return flatMap(workPackages, 'otherOwners');
  }, [workPackages]);

  return (
    <Box justifyContent={{ sm: 'space-between' }} px={{ sm: '600' }} pt={{ sm: '500' }} pb={{ sm: '400' }}>
      <Box data-testid="action-segment">
        <SegmentedControl segmentSize={SegmentedControlSize.SMALL}>
          <SegmentedControlOption
            selected={selectedIndex === 1}
            onClick={() => {
              setSelectedIndex(1);
              planningDispatch({ type: PlanningActionType.SetUnitOfMeasure, payload: { unitOfMeasure: 'HOURS' } });
            }}
            segmentSize={SegmentedControlSize.SMALL}
          >
            <Trans>Hours</Trans>
          </SegmentedControlOption>
          <SegmentedControlOption
            selected={selectedIndex === 2}
            onClick={() => {
              setSelectedIndex(2);
              planningDispatch({
                type: PlanningActionType.SetUnitOfMeasure,
                payload: { unitOfMeasure: UnitOfMeasure.LINEAR_FEET },
              });
            }}
            segmentSize={SegmentedControlSize.SMALL}
          >
            <Trans>Linear Feet</Trans>
          </SegmentedControlOption>
          <SegmentedControlOption
            selected={selectedIndex === 3}
            onClick={() => {
              setSelectedIndex(3);
              planningDispatch({ type: PlanningActionType.SetUnitOfMeasure, payload: { unitOfMeasure: 'MILESTONE' } });
            }}
            segmentSize={SegmentedControlSize.SMALL}
          >
            <Trans>Milestones</Trans>
          </SegmentedControlOption>
        </SegmentedControl>
      </Box>
      <Box data-testid="action-ownership">
        <Box mr={{ sm: '500' }}>
          <Button
            size={ButtonSize.SMALL}
            appearance={ButtonAppearance.SECONDARY}
            disabled={loading || (selectedWpList && selectedWpList.length > 0 ? false : true)}
            onClick={open}
            style={{ whiteSpace: 'nowrap' }}
            data-testid="edit-ownership-btn"
          >
            <Box mr={{ sm: '400' }} px={{ sm: '100' }}>
              <Icon icon="users" size={IconSize.LARGE} fillColor="blue.600" />
            </Box>
            <Text color="grey.1000" size="300">
              <Trans>Edit Ownership</Trans>
            </Text>
          </Button>
        </Box>
        <Box>
          <Button size={ButtonSize.SMALL} appearance={ButtonAppearance.SECONDARY} disabled={true}>
            <Icon icon="print" size={IconSize.MEDIUM} fillColor="blue.800" />
          </Button>
        </Box>
      </Box>
      <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
        {visible && (
          <AssignWorkOwner
            onClose={close}
            assignedCrewMembers={primaryOwners}
            secondaryOwners={secondaryOwners}
            otherOwners={otherOwners}
            workPackages={workPackages}
            isPlanningModule={true}
          />
        )}
      </Suspense>
    </Box>
  );
};
